<script setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  nextTick,
  watch,
} from 'vue';
import axios from '@/axiosConfig';
import eventBus from './eventBus.js';
import MotdModal from './MotdModal.vue'; // Import MOTD modal component
import { marked } from 'marked';
import { canOfferFirstLockCode } from '@/utils/lockCodeCreate';

const lockCodes = ref([]);
const searchQuery = ref('');
const selectedCode = ref(null);
const isEditing = ref(false);
const searchInputRef = ref(null);
const tenantCodeInputRef = ref(null);
const role = ref(null);
const showPhoneModal = ref(false);
const phoneNumber = ref('');
const pendingPropertySelection = ref(null);
const phoneError = ref('');
const isLoading = ref(false);
const nothingFound = ref(false);
const missingLockProperty = ref(null);
const phoneInputRef = ref(null);
const showNewCodeModal = ref(false);
const newCodeValue = ref('');
const newCodeType = ref(''); // 'tenant' or 'maint'
const showRequestsPopup = ref(false);
const lockCodeRequests = ref([]);
const requestsSearchQuery = ref('');
const titleTapCount = ref(0);
const activeView = ref('menu'); // 'menu', 'code-lookup', 'battery-tracker'

// Save-status toast (auto-hides)
const toastMessage = ref('');
const toastType = ref('success'); // 'success' | 'error'
const toastVisible = ref(false);
let toastTimer = null;
function showToast(message, type = 'success', duration = 2500) {
  toastMessage.value = message;
  toastType.value = type;
  toastVisible.value = true;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toastVisible.value = false;
  }, duration);
}

// ===== Battery Tracker State =====
const btProperties = ref([]);
const btFilterQuery = ref('');
const btSelectedCards = ref(new Set());
const btLoading = ref(false);
const btShowNotifyModal = ref(false);
const defaultEmailBody =
  '## Lock Battery Replacement Notice\n\n' +
  'Dear Tenant,\n\n' +
  'We will be replacing the lock batteries at your unit in the next few days. ' +
  'If you do not want maintenance to do this work in your absence, please use the link below to schedule a convenient time for this service.\n\n' +
  'Thank you for your cooperation.';
const btNotifyEmailBody = ref(defaultEmailBody);
const btNotifyFee = ref('25');
const btShowAddChildModal = ref(false);
const btAddChildPropertyId = ref(null);
const btAddChildParentLockId = ref(null);
const btAddChildLabel = ref('');
const btSending = ref(false);
const btNotifyResults = ref(null);
const btCollapsedGroups = ref(new Set()); // parentId set, all collapsed by default
const btShowNonRentable = ref(false); // false = hide non-rentable properties
const btDoneFilter = ref('all'); // 'all', 'done', 'not-done'
const btFocusedCard = ref(null); // propertyId of the card currently being edited
const btSortBySchedule = ref(false); // toggle sort by scheduled date

const lockTypeOptions = [
  'Dormakaba (Pump)',
  'Lockstate',
  'old: Morning Indus.',
  'Schlage Deadbolt',
  'Schlage exterior',
  'key style',
  'key padlock',
  'combo lock',
  'other',
];

// ===== Lock Note Popup State =====
const btShowNotePopup = ref(false);
const btNoteLockId = ref(null);
const btNoteText = ref('');
const btNoteAddress = ref('');
const btNoteLabel = ref('');

// ===== localStorage Persistence for Battery Tracker =====
const BT_STORAGE_KEY = 'bt_state';

function saveBtState() {
  try {
    localStorage.setItem(
      BT_STORAGE_KEY,
      JSON.stringify({
        filterQuery: btFilterQuery.value,
        doneFilter: btDoneFilter.value,
        showNonRentable: btShowNonRentable.value,
        collapsedGroups: Array.from(btCollapsedGroups.value),
        focusedCard: btFocusedCard.value,
      }),
    );
  } catch (e) {
    /* ignore */
  }
}

function loadBtState() {
  try {
    const raw = localStorage.getItem(BT_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    /* ignore */
  }
  return null;
}

// Restore saved filter/UI state
const _savedBt = loadBtState();
if (_savedBt) {
  btFilterQuery.value = _savedBt.filterQuery || '';
  btDoneFilter.value = _savedBt.doneFilter || 'all';
  btShowNonRentable.value = _savedBt.showNonRentable ?? false;
  btFocusedCard.value = _savedBt.focusedCard ?? null;
}

// Persist state changes to localStorage
watch(
  [
    btFilterQuery,
    btDoneFilter,
    btShowNonRentable,
    btCollapsedGroups,
    btFocusedCard,
  ],
  saveBtState,
);

function openLockNotePopup(lockId, currentNote, address, label) {
  btNoteLockId.value = lockId;
  btNoteText.value = currentNote || '';
  btNoteAddress.value = address || '';
  btNoteLabel.value = label || '';
  btShowNotePopup.value = true;
}

async function saveLockNote() {
  if (btNoteLockId.value == null) return;
  await updateDoorLock(btNoteLockId.value, { note: btNoteText.value });
  btShowNotePopup.value = false;
}

function closeLockNotePopup() {
  btShowNotePopup.value = false;
}

function goBackToMenu() {
  activeView.value = 'menu';
}

async function loadBatteryTrackerData() {
  btLoading.value = true;
  try {
    const { data } = await axios.get('/api/battery-tracker/properties', {
      params: { _t: Date.now() },
    });
    btProperties.value = data;
    // Restore collapsed state from localStorage, or collapse all by default
    const saved = loadBtState();
    if (saved && saved.collapsedGroups) {
      btCollapsedGroups.value = new Set(saved.collapsedGroups);
    } else {
      const collapsed = new Set();
      for (const p of data) {
        if (
          p.isParentAddr === 1 &&
          (p.parentId === p.propertyId || !p.parentId)
        ) {
          collapsed.add(p.propertyId);
        }
      }
      btCollapsedGroups.value = collapsed;
    }
  } catch (error) {
    console.error('Error loading battery tracker data:', error);
  } finally {
    btLoading.value = false;
  }
}

function toggleGroupCollapse(parentId) {
  const s = new Set(btCollapsedGroups.value);
  if (s.has(parentId)) {
    s.delete(parentId);
  } else {
    s.add(parentId);
  }
  btCollapsedGroups.value = s;
}

function isGroupCollapsed(parentId) {
  return btCollapsedGroups.value.has(parentId);
}

function collapseAllGroups() {
  const groups = btGroupedProperties.value;
  const allCollapsed = groups.every(
    (g) =>
      g.allChildren.length === 0 ||
      btCollapsedGroups.value.has(g.parent.propertyId),
  );
  if (allCollapsed) {
    btCollapsedGroups.value = new Set();
  } else {
    btCollapsedGroups.value = new Set(
      groups
        .filter((g) => g.allChildren.length > 0)
        .map((g) => g.parent.propertyId),
    );
  }
}

function getVisibleChildren(group) {
  const hasSearch = btFilterQuery.value.trim() !== '';
  if (hasSearch && !isGroupCollapsed(group.parent.propertyId)) {
    return group.allChildren;
  }
  return group.children;
}

// Properties after rentable filter (before search)
const btRentableProperties = computed(() => {
  let list = btProperties.value;
  if (!btShowNonRentable.value) {
    const parentIdsWithChildren = new Set(
      list
        .filter(
          (p) =>
            p.isRentable !== 0 &&
            p.parentId &&
            !(
              p.isParentAddr === 1 &&
              (p.parentId === p.propertyId || !p.parentId)
            ),
        )
        .map((p) => p.parentId),
    );
    list = list.filter(
      (p) =>
        p.isRentable !== 0 ||
        (p.isParentAddr === 1 && parentIdsWithChildren.has(p.propertyId)),
    );
  }
  return list;
});

const btFilteredProperties = computed(() => {
  let list = btRentableProperties.value;
  const q = btFilterQuery.value.toLowerCase().trim();
  if (!q) return list;
  // Split on comma for successive filters, each segment uses space-separated tokens
  const segments = q
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return list.filter((p) => {
    const lockTypes = (p.doorLocks || [])
      .map((l) => (l.lockType || '').toLowerCase())
      .join(' ');
    const text = (
      p.address +
      ' ' +
      (p.tenants || []).join(' ') +
      ' ' +
      lockTypes
    ).toLowerCase();
    return segments.every((seg) => {
      const tokens = seg.split(/\s+/);
      return tokens.every((t) => text.includes(t));
    });
  });
});

const btFilteredPropertyIds = computed(() => {
  return new Set(btFilteredProperties.value.map((p) => p.propertyId));
});

// Group properties: parent buildings with their children
const btGroupedProperties = computed(() => {
  const props = btFilteredProperties.value;
  const parentMap = new Map();
  const standalone = [];

  // First pass: identify parents (isParentAddr=1 with self-ref OR null parentId)
  for (const p of props) {
    if (p.isParentAddr === 1 && (p.parentId === p.propertyId || !p.parentId)) {
      parentMap.set(p.propertyId, { parent: p, children: [] });
    }
  }

  // Second pass: attach children
  for (const p of props) {
    if (p.isParentAddr === 1 && (p.parentId === p.propertyId || !p.parentId)) {
      // Already handled in first pass
      continue;
    }
    if (p.isParentAddr === 1 && p.parentId && p.parentId !== p.propertyId) {
      // Secondary parent structure (e.g. "Carriage") — attach to its parent
      if (parentMap.has(p.parentId)) {
        parentMap.get(p.parentId).children.push(p);
      } else {
        standalone.push({ parent: p, children: [] });
      }
    } else if (p.parentId && parentMap.has(p.parentId)) {
      parentMap.get(p.parentId).children.push(p);
    } else {
      standalone.push({ parent: p, children: [] });
    }
  }

  // Attach allChildren from full rentable list for expand-all-on-search
  const hasSearch = btFilterQuery.value.trim() !== '';
  if (hasSearch) {
    const allProps = btRentableProperties.value;
    for (const [parentId, group] of parentMap) {
      const filteredIds = new Set(group.children.map((c) => c.propertyId));
      const extras = allProps.filter(
        (p) =>
          p.parentId === parentId &&
          !(
            p.isParentAddr === 1 &&
            (p.parentId === p.propertyId || !p.parentId)
          ) &&
          !filteredIds.has(p.propertyId),
      );
      group.allChildren = [...group.children, ...extras];
    }
    for (const s of standalone) {
      s.allChildren = [...s.children];
    }
  } else {
    for (const [, group] of parentMap) group.allChildren = group.children;
    for (const s of standalone) s.allChildren = s.children;
  }

  return [...parentMap.values(), ...standalone]
    .sort((a, b) => {
      if (btSortBySchedule.value) {
        const schA = a.parent.scheduled?.date || '';
        const schB = b.parent.scheduled?.date || '';
        if (schA !== schB) {
          if (!schA) return 1;
          if (!schB) return -1;
          const cmp = schA.localeCompare(schB);
          if (cmp !== 0) return cmp;
        }
        const tA = a.parent.scheduled?.startTime || '';
        const tB = b.parent.scheduled?.startTime || '';
        if (tA !== tB) return tA.localeCompare(tB);
      }
      const rA = a.parent.route || '';
      const rB = b.parent.route || '';
      if (rA !== rB) {
        if (!rA) return 1;
        if (!rB) return -1;
        return rA.localeCompare(rB);
      }
      const sA = a.parent.routeSeq ?? Infinity;
      const sB = b.parent.routeSeq ?? Infinity;
      return sA - sB;
    })
    .map((group) => {
      if (btDoneFilter.value === 'all') return group;
      // Filter children to only those matching the done state
      const matchesFilter = (id) =>
        btDoneFilter.value === 'done'
          ? isPropertyDone(id)
          : !isPropertyDone(id);
      const filteredChildren = group.children.filter((c) =>
        matchesFilter(c.propertyId),
      );
      const parentMatches = matchesFilter(group.parent.propertyId);
      return {
        parent: group.parent,
        children: filteredChildren,
        allChildren: group.allChildren,
        parentMatches,
      };
    })
    .filter((group) => {
      if (btDoneFilter.value === 'all') return true;
      // Keep group if parent matches or any child matches
      return group.parentMatches || group.children.length > 0;
    });
});

function toggleCardSelection(propertyId) {
  const s = new Set(btSelectedCards.value);
  if (s.has(propertyId)) {
    s.delete(propertyId);
  } else {
    s.add(propertyId);
  }
  btSelectedCards.value = s;
}

function isCardSelected(propertyId) {
  return btSelectedCards.value.has(propertyId);
}

let btSavedCollapsedGroups = null; // saved state before auto-expanding

function cycleDoneFilter() {
  const cycle = { all: 'done', done: 'not-done', 'not-done': 'all' };
  const prev = btDoneFilter.value;
  btDoneFilter.value = cycle[prev];
  // Save collapsed state before first auto-expand, restore when returning to 'all'
  if (prev === 'all') {
    btSavedCollapsedGroups = new Set(btCollapsedGroups.value);
    btCollapsedGroups.value = new Set();
  } else if (btDoneFilter.value === 'all' && btSavedCollapsedGroups) {
    btCollapsedGroups.value = btSavedCollapsedGroups;
    btSavedCollapsedGroups = null;
  }
}

const btDoneFilterLabel = computed(() => {
  const labels = { all: 'All', done: 'Done', 'not-done': 'Not Done' };
  const label = labels[btDoneFilter.value];
  // Count individual locks matching the current filter
  let count = 0;
  for (const p of btFilteredProperties.value) {
    const locks = p.doorLocks || [];
    for (const l of locks) {
      if (l.deleted) continue;
      if (btDoneFilter.value === 'all') count++;
      else if (btDoneFilter.value === 'done' && l.batteryReplaced === 1)
        count++;
      else if (btDoneFilter.value === 'not-done' && l.batteryReplaced !== 1)
        count++;
    }
  }
  return `${label} (${count})`;
});

function isPropertyDone(propertyId) {
  const { primary, children } = getDoorLocksForProperty(propertyId);
  const locks = [...(primary ? [primary] : []), ...children];
  if (locks.length === 0) return false;
  return locks.every((l) => l.batteryReplaced === 1);
}

function lockMatchesFilter(lock, propertyAddress, propertyTenants, propertyId) {
  // Done/not-done filter
  if (btDoneFilter.value !== 'all') {
    const isDone = lock.batteryReplaced === 1;
    if (btDoneFilter.value === 'done' ? !isDone : isDone) return false;
  }
  // Skip search filter for properties shown via expand-all-children
  if (propertyId && !btFilteredPropertyIds.value.has(propertyId)) return true;
  // Search filter: check this individual lock against search query
  const q = btFilterQuery.value.toLowerCase().trim();
  if (!q) return true;
  const segments = q
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const text = (
    (propertyAddress || '') +
    ' ' +
    (propertyTenants || []).join(' ') +
    ' ' +
    (lock.lockType || '') +
    ' ' +
    (lock.locationLabel || '')
  ).toLowerCase();
  return segments.every((seg) => {
    const tokens = seg.split(/\s+/);
    return tokens.every((t) => text.includes(t));
  });
}

async function refreshPropertyData(propertyId) {
  try {
    const { data } = await axios.get(
      `/api/battery-tracker/properties/${propertyId}`,
      { params: { _t: Date.now() } },
    );
    const prop = btProperties.value.find((p) => p.propertyId === propertyId);
    if (prop) {
      prop.doorLocks = data.doorLocks;
      prop.tenants = data.tenants;
      prop.scheduled = data.scheduled;
    }
  } catch (e) {
    console.error('Error refreshing property:', e);
  }
}

async function updatePropertyRoute(propertyId, field, value) {
  try {
    const update = {};
    update[field] = value || null;
    await axios.put(`/api/battery-tracker/properties/${propertyId}`, update);
    const prop = btProperties.value.find((p) => p.propertyId === propertyId);
    if (prop) prop[field] = value || null;
  } catch (e) {
    console.error('Error updating property route:', e);
  }
}

function toggleFocusedCard(propertyId, event) {
  if (event) {
    const tag = event.target.tagName.toLowerCase();
    if (
      ['input', 'select', 'button', 'label', 'textarea', 'option'].includes(
        tag,
      ) ||
      event.target.closest('button, label')
    ) {
      return;
    }
  }
  const newValue = btFocusedCard.value === propertyId ? null : propertyId;
  btFocusedCard.value = newValue;
  if (newValue !== null) {
    refreshPropertyData(newValue);
  }
}

function isCardFocused(propertyId) {
  return btFocusedCard.value === propertyId;
}

function formatScheduled(scheduled) {
  if (!scheduled) return '';
  // Date may be ISO string "2026-02-18T05:00:00.000Z" or "2026-02-18"
  const dateStr = String(scheduled.date).split('T')[0];
  const [yr, mo, dy] = dateStr.split('-');
  const month = parseInt(mo);
  const day = parseInt(dy);
  const year = yr.slice(2);
  // Parse start_time "HH:MM:SS"
  const [h, m] = scheduled.startTime.split(':');
  let hour = parseInt(h);
  const ampm = hour >= 12 ? 'pm' : 'am';
  if (hour > 12) hour -= 12;
  if (hour === 0) hour = 12;
  const timeStr = m === '00' ? `${hour}${ampm}` : `${hour}:${m}${ampm}`;
  return `(sched. ${month}/${day}/${year} ${timeStr})`;
}

function getDoorLocksForProperty(propertyId) {
  const prop = btProperties.value.find((p) => p.propertyId === propertyId);
  if (!prop) return { primary: null, children: [] };
  const locks = prop.doorLocks || [];
  const primary = locks.find((l) => !l.parentDoorLockId) || null;
  const children = locks.filter((l) => l.parentDoorLockId);
  return { primary, children };
}

async function createDoorLock(propertyId, parentDoorLockId, locationLabel) {
  try {
    const { data } = await axios.post('/api/battery-tracker/door-locks', {
      propertyId,
      parentDoorLockId: parentDoorLockId || null,
      locationLabel: locationLabel || null,
    });
    // Refresh data
    await loadBatteryTrackerData();
    return data;
  } catch (error) {
    console.error('Error creating door lock:', error);
  }
}

async function updateDoorLock(lockId, updates) {
  try {
    await axios.put(`/api/battery-tracker/door-locks/${lockId}`, updates);
    // Update local data
    for (const prop of btProperties.value) {
      const lock = (prop.doorLocks || []).find((l) => l.id === lockId);
      if (lock) {
        Object.assign(lock, updates);
        if (updates.batteryReplaced === 1 && !updates.batteryReplacedDate) {
          const now = new Date();
          lock.batteryReplacedDate =
            now.getFullYear() +
            '-' +
            String(now.getMonth() + 1).padStart(2, '0') +
            '-' +
            String(now.getDate()).padStart(2, '0');
        }
        break;
      }
    }
  } catch (error) {
    console.error('Error updating door lock:', error);
  }
}

async function deleteDoorLock(lockId) {
  if (!confirm('Delete this door lock entry?')) return;
  try {
    await axios.delete(`/api/battery-tracker/door-locks/${lockId}`);
    await loadBatteryTrackerData();
  } catch (error) {
    console.error('Error deleting door lock:', error);
  }
}

function openAddChildModal(propertyId, parentLockId) {
  btAddChildPropertyId.value = propertyId;
  btAddChildParentLockId.value = parentLockId;
  btAddChildLabel.value = '';
  btShowAddChildModal.value = true;
}

async function saveChildDoorLock() {
  if (!btAddChildLabel.value.trim()) return;
  await createDoorLock(
    btAddChildPropertyId.value,
    btAddChildParentLockId.value,
    btAddChildLabel.value.trim(),
  );
  btShowAddChildModal.value = false;
}

async function ensurePrimaryLock(propertyId) {
  const { primary } = getDoorLocksForProperty(propertyId);
  if (!primary) {
    return await createDoorLock(propertyId, null, null);
  }
  return primary;
}

function openNotifyModal() {
  if (btSelectedCards.value.size === 0) {
    alert('Please select at least one property card first.');
    return;
  }
  // Expand: if a parent is selected, replace it with its children
  const expanded = new Set(btSelectedCards.value);
  for (const group of btGroupedProperties.value) {
    if (expanded.has(group.parent.propertyId) && group.children.length > 0) {
      expanded.delete(group.parent.propertyId);
      for (const child of group.children) {
        expanded.add(child.propertyId);
      }
    }
  }
  btSelectedCards.value = expanded;
  btNotifyResults.value = null;
  btShowNotifyModal.value = true;
}

async function sendNotifications() {
  btSending.value = true;
  try {
    const { data } = await axios.post('/api/battery-tracker/notify', {
      propertyIds: Array.from(btSelectedCards.value),
      emailBody: btNotifyEmailBody.value,
      fee: btNotifyFee.value,
    });
    btNotifyResults.value = data.results;
  } catch (error) {
    console.error('Error sending notifications:', error);
    alert('Error sending notifications. Check console for details.');
  } finally {
    btSending.value = false;
  }
}

function closeNotifyModal() {
  btShowNotifyModal.value = false;
  btNotifyResults.value = null;
}

const btEmailPreviewHtml = computed(() => {
  const body = marked(btNotifyEmailBody.value || '');
  const feeDisplay = btNotifyFee.value
    ? `$${parseFloat(btNotifyFee.value).toFixed(2)}`
    : '$0.00';
  const scheduleLink = 'http://pixiev2:8080/schedule?type=battery';
  const sampleCode = 'A1B2C3D4';
  const codeHtml = sampleCode
    .split('')
    .map(
      (ch) =>
        `<span style="display:inline-block;width:28px;height:36px;border:2px solid #6e5875;border-radius:4px;text-align:center;line-height:36px;font-family:'Courier New',monospace;font-size:18px;font-weight:bold;margin:0 2px;color:#333;">${ch}</span>`,
    )
    .join('');
  return `
    <div style="font-family: Arial, sans-serif; max-width: 100%;">
      <p style="font-size: 11px; color: #b088b8; font-style: italic; margin: 0 0 8px 0;">⚠ Each property receives a unique code. Sample shown below.</p>
      ${body}
      <p style="font-size: 14px; color: #555;">The fee for this service is ${feeDisplay}.</p>
      <div style="margin: 24px 0; padding: 16px; background: #f8f5fa; border-radius: 8px; text-align: center;">
        <p style="margin: 0 0 12px 0; font-size: 14px; color: #666;">Your scheduling code:</p>
        <div style="margin: 12px 0;">${codeHtml}</div>
        <p style="margin: 12px 0 0 0; font-size: 13px; color: #888;">Enter this code when prompted at the scheduling page.</p>
      </div>
      <div style="text-align: center; margin: 24px 0;">
        <a href="${scheduleLink}" style="display:inline-block;padding:12px 32px;background:#6e5875;color:#fff;text-decoration:none;border-radius:6px;font-size:16px;">Schedule Battery Replacement</a>
      </div>
    </div>
  `;
});

const btFormatDate = (dateStr) => {
  if (!dateStr) return '--/--/--';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '--/--/--';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return mm + '/' + dd + '/' + yy;
};

function handleEscKey(e) {
  if (e.key === 'Escape' && activeView.value === 'battery-tracker') {
    // Clear search filter
    btFilterQuery.value = '';
    // Collapse all parent groups
    const collapsed = new Set();
    for (const p of btProperties.value) {
      if (
        p.isParentAddr === 1 &&
        (p.parentId === p.propertyId || !p.parentId)
      ) {
        collapsed.add(p.propertyId);
      }
    }
    btCollapsedGroups.value = collapsed;
  }
}

onMounted(async () => {
  window.addEventListener('keydown', handleEscKey);
  const results = await axios.get('/api/user-role');
  console.log('results:', results);
  role.value = results.data[0]?.role;
  const { data } = await axios.get('/api/lock-code-addr', {
    params: { _ts: Date.now() },
  });
  lockCodes.value = data;

  // Log user location
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        axios.post('/api/log-location', {
          componentName: 'MLockCodes',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        axios.post('/api/log-location', {
          componentName: 'MLockCodes',
          locationError: error.message,
        });
      },
    );
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEscKey);
});

function filterResults() {
  selectedCode.value = null;
  nothingFound.value = false;
  missingLockProperty.value = null;
}

const filteredLockCodes = computed(() => {
  const q = searchQuery.value.toLowerCase();
  if (q === '') return [];

  // Replace commas with spaces, then split by spaces and filter out empty tokens
  const tokens = q
    .replace(/,/g, ' ')
    .split(' ')
    .map((token) => token.trim())
    .filter((token) => token);

  return lockCodes.value.filter((item) => {
    // Combine address and note into a single string for matching
    const fullText = (item.address + ' ' + (item.note || '')).toLowerCase();

    // Create a temporary copy of the text that we'll modify as we match tokens
    let remainingText = fullText;

    // Check each token sequentially
    for (const token of tokens) {
      const index = remainingText.indexOf(token);
      if (index === -1) {
        // This token doesn't match the remaining text
        return false;
      }

      // "Remove" the matched part and everything before it from the remaining text
      // by creating a new substring starting after the match
      remainingText = remainingText.substring(index + token.length);
    }

    return true;
  });
});

function highlightMatch(text, queryInput) {
  if (queryInput) {
    const tokens = queryInput.split(',').map((token) => token.trim());
    const query = tokens.length === 1 ? tokens[0] : tokens[tokens.length - 1];
    const escapedQuery = query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    if (text) {
      return text.replace(regex, '<span class="highlight">$1</span>');
    }
  }
  return text;
}

function handleTitleTap() {
  console.log('Title tapped, role:', role.value, 'type:', typeof role.value);
  console.log('Tap count:', titleTapCount.value);

  if (role.value != 1) {
    console.log('Not admin, returning');
    return; // Only for admin users
  }

  titleTapCount.value++;
  console.log('Incremented tap count to:', titleTapCount.value);

  if (titleTapCount.value === 3) {
    console.log('Three taps detected, fetching requests');
    fetchLockCodeRequests();
    titleTapCount.value = 0;
  }
}

async function fetchLockCodeRequests() {
  try {
    console.log('Fetching lock code requests...');
    const { data } = await axios.get('/api/lock-code-requests/all');
    console.log('Received data:', data);
    lockCodeRequests.value = data;
    console.log('lockCodeRequests.value set to:', lockCodeRequests.value);
    showRequestsPopup.value = true;
    console.log('showRequestsPopup set to:', showRequestsPopup.value);
  } catch (error) {
    console.error('Error fetching lock code requests:', error);
  }
}

function closeRequestsPopup() {
  showRequestsPopup.value = false;
  requestsSearchQuery.value = '';
}

const filteredRequests = computed(() => {
  const q = requestsSearchQuery.value.toLowerCase();
  if (!q) return lockCodeRequests.value;

  return lockCodeRequests.value.filter((request) => {
    const searchText =
      `${request.address || ''} ${request.displayName || ''} ${request.id || ''}`.toLowerCase();
    return searchText.includes(q);
  });
});

async function updateLockCode(field) {
  if (!selectedCode.value) return false;
  try {
    const { data } = await axios.put(
      `/api/lock-codes/${selectedCode.value.id}`,
      {
        [field]: selectedCode.value[field],
      },
    );

    // Update modifiedOn / displayName from server response
    if (data.modifiedOn) {
      selectedCode.value.modifiedOn = data.modifiedOn;
    }
    if (data.displayName) {
      selectedCode.value.displayName = data.displayName;
    }

    // Update the local lockCodes array with the edited value
    const index = lockCodes.value.findIndex(
      (code) => code.id === selectedCode.value.id,
    );
    if (index !== -1) {
      lockCodes.value[index][field] = selectedCode.value[field];
    }

    const labelMap = {
      tenantCode: 'Tenant code',
      maintCode: 'Maintenance code',
      note: 'Note',
      slotNumber: 'Slot number',
      slotNumberOrDate: 'Slot/Date',
    };
    showToast(`${labelMap[field] || field} saved`, 'success');
    return true;
  } catch (error) {
    console.error('Error updating lock code:', error);
    const status = error?.response?.status;
    const msg =
      status === 403
        ? 'Not allowed to save (permission denied)'
        : 'Save failed — please try again';
    showToast(msg, 'error', 4000);
    return false;
  }
}

async function updateBatteryChanged() {
  if (!selectedCode.value) return;
  try {
    const { data } = await axios.put(
      `/api/lock-codes/${selectedCode.value.id}`,
      {
        batteryChanged: selectedCode.value.batteryChanged,
      },
    );

    // Update modifiedOn / displayName from server response
    if (data.modifiedOn) {
      selectedCode.value.modifiedOn = data.modifiedOn;
    }
    if (data.displayName) {
      selectedCode.value.displayName = data.displayName;
    }

    // Update the local lockCodes array with the edited value
    const index = lockCodes.value.findIndex(
      (code) => code.id === selectedCode.value.id,
    );
    if (index !== -1) {
      lockCodes.value[index].batteryChanged = selectedCode.value.batteryChanged;
    }
    showToast('Battery date saved', 'success');
  } catch (error) {
    console.error('Error updating battery changed date:', error);
    const status = error?.response?.status;
    const msg =
      status === 403
        ? 'Not allowed to save (permission denied)'
        : 'Save failed — please try again';
    showToast(msg, 'error', 4000);
  }
}

// async function logRequest(code) {
//   if (!code || (!code.propertyId && !code.id)) {
//     console.error('Invalid code or propertyId missing', code);
//     return;
//   }
//   try {
//     // Use propertyId if available, otherwise fall back to id
//     const propertyId = code.propertyId || code.id;
//     console.log(`Logging request for propertyId: ${propertyId}`);
//     await axios.post('/api/log-code-request', {
//       propertyId: propertyId,
//     });
//   } catch (error) {
//     console.error('Error logging lock code:', error);
//   }
// }

async function selectCode(code) {
  // Reset states
  selectedCode.value = null;
  nothingFound.value = false;
  phoneError.value = '';
  phoneNumber.value = '';

  try {
    // First, check if user has valid phone authentication session on server
    console.log('Checking phone auth status before requesting lock codes');
    const authCheckResponse = await axios.get('/api/check-phone-auth');

    if (authCheckResponse.data.hasValidAuth) {
      console.log(
        'User has valid phone auth session, requesting lock codes directly',
      );

      // User has valid session, request lock codes directly
      const response = await axios.get('/api/lock-codes', {
        params: {
          propertyId: code.id,
          // Don't send phoneNumber parameter - server knows session is valid
          _ts: Date.now(),
        },
      });

      console.log('Lock codes API response:', response.data);

      if (response.data && Object.keys(response.data).length > 0) {
        selectedCode.value = { ...response.data };
        nothingFound.value = false;
        missingLockProperty.value = null;

        // Server already logs the request, no need to log again on client
        return;
      } else {
        console.log('No lock code data returned');
        showNothingFound(code);
        return;
      }
    } else {
      // No server session - check localStorage for admin/poweruser bypass
      if (role.value <= 2) {
        const lastPhoneVerification = localStorage.getItem(
          'lockCodesPhoneVerification',
        );
        const storedPhone = localStorage.getItem('lockCodesPhoneNumber');

        if (lastPhoneVerification && storedPhone) {
          const verificationTime = parseInt(lastPhoneVerification, 10);
          const twoHoursInMs = 2 * 60 * 60 * 1000;
          const now = Date.now();

          if (now - verificationTime < twoHoursInMs) {
            console.log(
              'Admin/poweruser with recent phone verification, auto-submitting phone',
            );

            // Directly fetch lock codes with stored phone number
            try {
              const response = await axios.get('/api/lock-codes', {
                params: {
                  propertyId: code.id,
                  phoneNumber: storedPhone,
                  _ts: Date.now(),
                },
              });

              if (response.data && Object.keys(response.data).length > 0) {
                selectedCode.value = { ...response.data };
                nothingFound.value = false;
                missingLockProperty.value = null;
                return;
              } else {
                showNothingFound(code);
                return;
              }
            } catch (error) {
              console.error('Error with stored phone number:', error);
              // Fall through to show phone modal
            }
          }
        }
      }

      console.log(
        'User does not have valid phone auth session, showing phone modal',
      );
      // No valid session, show phone modal
      pendingPropertySelection.value = code;
      showPhoneModal.value = true;

      // Focus the phone input after modal is shown
      await nextTick();
      if (phoneInputRef.value) {
        phoneInputRef.value.focus();
      }
    }
  } catch (error) {
    console.error('Error checking auth status or fetching lock codes:', error);

    // If auth check fails, fall back to phone modal
    console.log('Auth check failed, showing phone modal as fallback');
    pendingPropertySelection.value = code;
    showPhoneModal.value = true;

    // Focus the phone input after modal is shown
    await nextTick();
    if (phoneInputRef.value) {
      phoneInputRef.value.focus();
    }
  }
}

function validatePhoneNumber(phone) {
  // Remove any non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 && /^\d{10}$/.test(cleaned);
}

async function submitPhoneNumber() {
  phoneError.value = '';

  if (!validatePhoneNumber(phoneNumber.value)) {
    phoneError.value = 'Please enter a valid 10-digit phone number';
    return;
  }

  const cleanedPhone = phoneNumber.value.replace(/\D/g, '');
  isLoading.value = true;

  try {
    const response = await axios.get('/api/lock-codes', {
      params: {
        propertyId: pendingPropertySelection.value.id,
        phoneNumber: cleanedPhone,
        _ts: Date.now(),
      },
    });

    // Close modal first
    showPhoneModal.value = false;

    if (response.data && Object.keys(response.data).length > 0) {
      // Success - we got lock code data
      selectedCode.value = { ...response.data };
      nothingFound.value = false;
      missingLockProperty.value = null;

      // Store phone verification timestamp for admin/poweruser
      if (role.value <= 2) {
        localStorage.setItem(
          'lockCodesPhoneVerification',
          Date.now().toString(),
        );
        localStorage.setItem('lockCodesPhoneNumber', cleanedPhone);
        console.log(
          'Phone verification timestamp and number stored for admin/poweruser',
        );
      }

      // Server already logs the request, no need to log again on client
    } else {
      // Empty response - show nothing found
      showNothingFound(pendingPropertySelection.value);
    }
  } catch (error) {
    console.error('Error fetching lock code:', error);
    showPhoneModal.value = false;
    showNothingFound(null);
  } finally {
    isLoading.value = false;
    pendingPropertySelection.value = null;
  }
}

function cancelPhoneModal() {
  showPhoneModal.value = false;
  phoneNumber.value = '';
  phoneError.value = '';
  pendingPropertySelection.value = null;
}

function showNothingFound(property) {
  selectedCode.value = null;
  nothingFound.value = true;
  missingLockProperty.value =
    property && property.id
      ? { id: property.id, address: property.address || '' }
      : null;
}

async function createFirstLockCode() {
  const property = missingLockProperty.value;
  if (
    !property ||
    !canOfferFirstLockCode({
      nothingFound: nothingFound.value,
      role: role.value,
      propertyId: property.id,
    })
  ) {
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await axios.post('/api/lock-codes', {
      propertyId: property.id,
    });
    if (!data || !data.id) {
      showToast('Could not add lock code', 'error', 4000);
      return;
    }
    selectedCode.value = { ...data };
    nothingFound.value = false;
    missingLockProperty.value = null;
    isEditing.value = true;
    showToast('Lock code added', 'success');
    await nextTick();
    if (tenantCodeInputRef.value) {
      tenantCodeInputRef.value.focus();
    }
  } catch (error) {
    console.error('Error creating lock code:', error);
    showToast('Could not add lock code', 'error', 4000);
  } finally {
    isLoading.value = false;
  }
}

function clearSearch() {
  searchQuery.value = '';
  selectedCode.value = null;
  nothingFound.value = false;
  missingLockProperty.value = null;
  showPhoneModal.value = false;
  phoneNumber.value = '';
  phoneError.value = '';
  pendingPropertySelection.value = null;
}

// Refresh both the address list and the currently-viewed code from the
// server. Cache-busting _ts params defeat any PWA / proxy cache so a
// code edited on another device shows up immediately.
async function refreshLockCodes() {
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    const { data } = await axios.get('/api/lock-code-addr', {
      params: { _ts: Date.now() },
    });
    lockCodes.value = data;

    if (selectedCode.value && selectedCode.value.id) {
      const params = { propertyId: selectedCode.value.id, _ts: Date.now() };
      // Match the auth path used by selectCode(): admin/poweruser with a
      // recent local verification can pass the stored phone directly.
      if (role.value <= 2) {
        const lastPhoneVerification = localStorage.getItem(
          'lockCodesPhoneVerification',
        );
        const storedPhone = localStorage.getItem('lockCodesPhoneNumber');
        if (lastPhoneVerification && storedPhone) {
          const verificationTime = parseInt(lastPhoneVerification, 10);
          if (Date.now() - verificationTime < 2 * 60 * 60 * 1000) {
            params.phoneNumber = storedPhone;
          }
        }
      }
      try {
        const response = await axios.get('/api/lock-codes', { params });
        if (response.data && Object.keys(response.data).length > 0) {
          selectedCode.value = { ...response.data };
        }
      } catch (err) {
        console.error('Error refreshing selected code:', err);
      }
    }
  } catch (error) {
    console.error('Error refreshing lock codes:', error);
  } finally {
    isLoading.value = false;
  }
}

async function toggleEditMode() {
  if (isEditing.value) {
    // User is stopping editing: turn off edit mode and focus the search input.
    isEditing.value = false;
    await nextTick();
    if (searchInputRef.value) {
      searchInputRef.value.focus();
      searchInputRef.value.select();
    }
  } else {
    // Entering editing mode: focus the tenant code input.
    isEditing.value = true;
    await nextTick();
    if (tenantCodeInputRef.value) {
      tenantCodeInputRef.value.focus();
      tenantCodeInputRef.value.select();
    }
  }
}

function generateRandomCode() {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

function openNewCodeModal(type) {
  newCodeType.value = type;
  newCodeValue.value = generateRandomCode();
  showNewCodeModal.value = true;
}

function cancelNewCode() {
  showNewCodeModal.value = false;
  newCodeValue.value = '';
  newCodeType.value = '';
}

/** Snapshot current tenant/maint codes for the note (e.g. "t: 123456; m: 789123"). */
function formatPreviousCodesSnapshot(code) {
  const t = (code?.tenantCode ?? '').toString().trim();
  const m = (code?.maintCode ?? '').toString().trim();
  return `t: ${t}; m: ${m}`;
}

/**
 * Prepend prior t/m codes to the note before a New code replaces one of them.
 * Does not re-prepend if the note already starts with the same snapshot.
 */
function prependPreviousCodesToNote() {
  if (!selectedCode.value) return false;
  const snapshot = formatPreviousCodesSnapshot(selectedCode.value);
  // Nothing meaningful to archive
  if (snapshot === 't: ; m: ') return false;

  const existing = (selectedCode.value.note || '').toString();
  const existingTrim = existing.trimStart();
  if (
    existingTrim === snapshot ||
    existingTrim.startsWith(snapshot + ' ') ||
    existingTrim.startsWith(snapshot + '\n')
  ) {
    return false;
  }

  selectedCode.value.note = existingTrim
    ? `${snapshot} ${existingTrim}`
    : snapshot;
  return true;
}

async function saveNewCode() {
  const field = newCodeType.value === 'tenant' ? 'tenantCode' : 'maintCode';
  const value = newCodeValue.value;

  if (!selectedCode.value) {
    showNewCodeModal.value = false;
    newCodeValue.value = '';
    newCodeType.value = '';
    return;
  }

  // Archive previous tenant + maintenance codes into the note first
  const noteUpdated = prependPreviousCodesToNote();

  // Apply the generated value locally first so the UI reflects it
  // whether or not the save succeeds.
  selectedCode.value[field] = value;

  // Switch to edit mode if not already
  if (!isEditing.value) {
    isEditing.value = true;
  }

  // Persist note (if changed) then the new code — previously this only
  // mutated local state and relied on a subsequent blur to trigger the PUT.
  if (noteUpdated) {
    await updateLockCode('note');
  }
  await updateLockCode(field);

  showNewCodeModal.value = false;
  newCodeValue.value = '';
  newCodeType.value = '';
}

const isMobile = computed(() => window.innerWidth <= 500);
const isSearching = computed(() => selectedCode.value != null);
const canCreateLockCode = computed(() =>
  canOfferFirstLockCode({
    nothingFound: nothingFound.value,
    role: role.value,
    propertyId: missingLockProperty.value && missingLockProperty.value.id,
  }),
);
const sendClose = () => eventBus.emit('requestCloseLockCodes');

const batteryChangedDate = computed({
  get() {
    if (!selectedCode.value?.batteryChanged) return '';
    // Convert datetime to YYYY-MM-DD format for date input
    const date = new Date(selectedCode.value.batteryChanged);
    return date.toISOString().split('T')[0];
  },
  set(value) {
    if (selectedCode.value) {
      selectedCode.value.batteryChanged = value ? value : null;
    }
  },
});

const formatDate = (dateString) => {
  if (dateString === null || dateString === undefined) {
    return '--/--/--';
  }
  const options = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};
</script>

<template>
  <div class="lock-codes-page">
    <!-- Save-status toast -->
    <transition name="lc-toast">
      <div
        v-if="toastVisible"
        class="lc-toast"
        :class="toastType === 'error' ? 'lc-toast-error' : 'lc-toast-success'"
        role="status"
        aria-live="polite"
      >
        <i
          :class="
            toastType === 'error'
              ? 'fas fa-exclamation-triangle'
              : 'fas fa-check-circle'
          "
        ></i>
        <span>{{ toastMessage }}</span>
      </div>
    </transition>
    <!-- MOTD Modal Component -->
    <MotdModal :route="$route.path" />

    <!-- ===== MAIN MENU VIEW ===== -->
    <div v-if="activeView === 'menu'">
      <div class="title-bar">
        <span style="flex: 1">Lock Tools</span>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
      <div class="menu-container">
        <button
          class="menu-button"
          @click="
            activeView = 'battery-tracker';
            loadBatteryTrackerData();
          "
        >
          <i class="fas fa-battery-half menu-icon"></i>
          <span>Battery Tracker</span>
        </button>
        <button class="menu-button" @click="activeView = 'code-lookup'">
          <i class="fas fa-lock menu-icon"></i>
          <span>Code Lookup</span>
        </button>
      </div>
    </div>

    <!-- ===== BATTERY TRACKER VIEW ===== -->
    <div v-if="activeView === 'battery-tracker'" class="bt-view">
      <!-- Add Child Door Lock Modal -->
      <div v-if="btShowAddChildModal" class="modal-overlay">
        <div class="bt-child-modal">
          <h3>Add Door Lock</h3>
          <p>Enter a label (e.g. front, back, basement, garage):</p>
          <input
            v-model="btAddChildLabel"
            type="text"
            placeholder="e.g. back door"
            class="bt-child-input"
            @keyup.enter="saveChildDoorLock"
          />
          <div class="modal-buttons">
            <button @click="btShowAddChildModal = false" class="cancel-button">
              Cancel
            </button>
            <button @click="saveChildDoorLock" class="submit-button">
              Add
            </button>
          </div>
        </div>
      </div>

      <!-- Lock Note Popup -->
      <div
        v-if="btShowNotePopup"
        class="bt-note-popup-overlay"
        @click="closeLockNotePopup"
      ></div>
      <div v-if="btShowNotePopup" class="bt-note-popup">
        <h4><i class="fas fa-sticky-note"></i> Lock Note</h4>
        <div class="bt-note-context">
          {{ btNoteAddress
          }}<span v-if="btNoteLabel"> — {{ btNoteLabel }}</span>
        </div>
        <textarea
          v-model="btNoteText"
          placeholder="Enter a note for this lock..."
          @keydown.ctrl.enter="saveLockNote"
        ></textarea>
        <div class="bt-note-popup-buttons">
          <button @click="saveLockNote">Save</button>
          <button @click="closeLockNotePopup">Cancel</button>
        </div>
      </div>

      <!-- Notify / Schedule Modal -->
      <div v-if="btShowNotifyModal" class="modal-overlay">
        <div class="bt-notify-modal">
          <h3>
            Notify / Schedule
            <span class="bt-notify-count">
              ({{ btSelectedCards.size }}
              {{ btSelectedCards.size === 1 ? 'property' : 'properties' }})
            </span>
          </h3>
          <div v-if="!btNotifyResults">
            <label class="bt-label">Email Body (Markdown):</label>
            <textarea
              v-model="btNotifyEmailBody"
              class="bt-email-textarea"
              rows="8"
            ></textarea>
            <label class="bt-label" style="margin-top: 12px">Preview:</label>
            <div class="bt-email-preview" v-html="btEmailPreviewHtml"></div>
            <label class="bt-label">Fee:</label>
            <select v-model="btNotifyFee" class="bt-fee-select">
              <option value="25">$25</option>
              <option value="35">$35</option>
              <option value="50">$50</option>
              <option value="75">$75</option>
              <option value="100">$100</option>
            </select>
            <div class="bt-notify-info">
              Sending to {{ btSelectedCards.size }} selected
              {{ btSelectedCards.size === 1 ? 'property' : 'properties' }}
            </div>
            <div class="modal-buttons">
              <button @click="closeNotifyModal" class="cancel-button">
                Cancel
              </button>
              <button
                @click="sendNotifications"
                :disabled="btSending"
                class="submit-button"
              >
                {{ btSending ? 'Sending...' : 'Send Notifications' }}
              </button>
            </div>
          </div>
          <div v-else class="bt-notify-results">
            <h4>Results:</h4>
            <div
              v-for="(r, i) in btNotifyResults"
              :key="i"
              class="bt-notify-result-item"
            >
              <div>
                <span>{{ r.address }}</span>
                <span
                  v-if="r.emails && r.emails.length"
                  style="color: #999; font-size: 0.85em"
                >
                  ({{ r.emails.length }}
                  {{ r.emails.length === 1 ? 'tenant' : 'tenants' }})
                </span>
              </div>
              <span
                :class="{
                  'status-sent': r.status === 'sent',
                  'status-failed': r.status === 'failed',
                  'status-none': r.status === 'no_tenants',
                }"
              >
                {{
                  r.status === 'sent'
                    ? '✓ Sent'
                    : r.status === 'no_tenants'
                      ? 'No tenants'
                      : '✗ Failed'
                }}
              </span>
            </div>
            <div class="modal-buttons" style="margin-top: 16px">
              <button @click="closeNotifyModal" class="submit-button">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span style="flex: 1">Battery Tracker</span>
        <button class="home" @click="loadBatteryTrackerData" title="Refresh">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>

      <div class="bt-content">
        <!-- Filter bar -->
        <div class="bt-filter-bar">
          <input
            v-model="btFilterQuery"
            placeholder="Filter by address or tenant..."
            class="bt-filter-input"
          />
          <button class="clear-search" @click="btFilterQuery = ''">
            <i class="fas fa-backspace"></i>
          </button>
        </div>

        <!-- Filter buttons row -->
        <div class="bt-button-row">
          <button
            class="bt-filter-btn"
            :class="{ 'bt-filter-active': btShowNonRentable }"
            @click="btShowNonRentable = !btShowNonRentable"
            :title="
              btShowNonRentable
                ? 'Showing all properties'
                : 'Hiding non-rentable'
            "
          >
            <i
              class="fas"
              :class="btShowNonRentable ? 'fa-eye' : 'fa-eye-slash'"
            ></i>
            {{ btShowNonRentable ? 'All' : 'Rentable' }}
          </button>
          <button
            class="bt-filter-btn"
            :class="{ 'bt-filter-active': btDoneFilter !== 'all' }"
            @click="cycleDoneFilter"
            :title="'Filter: ' + btDoneFilterLabel"
          >
            <i
              class="fas"
              :class="{
                'fa-list': btDoneFilter === 'all',
                'fa-check-circle': btDoneFilter === 'done',
                'fa-circle-xmark': btDoneFilter === 'not-done',
              }"
            ></i>
            {{ btDoneFilterLabel }}
          </button>
        </div>

        <!-- Action buttons row -->
        <div class="bt-button-row">
          <button class="bt-action-btn" @click="openNotifyModal">
            <i class="fas fa-envelope"></i> Notify/Schedule
          </button>
          <button class="bt-action-btn" @click="collapseAllGroups">
            <i class="fas fa-compress-alt"></i> Collapse All
          </button>
          <button
            class="bt-action-btn"
            :class="{ 'bt-action-active': btSortBySchedule }"
            @click="btSortBySchedule = !btSortBySchedule"
          >
            <i class="fas fa-calendar-alt"></i> Sort Sched
          </button>
        </div>

        <!-- Loading -->
        <div v-if="btLoading" class="bt-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading properties...
        </div>

        <!-- Scrollable card list -->
        <div v-else class="bt-card-list">
          <div
            v-for="group in btGroupedProperties"
            :key="group.parent.propertyId"
            class="bt-group"
          >
            <!-- Parent property card -->
            <div
              class="bt-card"
              :class="{
                'bt-card-selected': isCardSelected(group.parent.propertyId),
                'bt-card-focused': isCardFocused(group.parent.propertyId),
              }"
              @click="toggleFocusedCard(group.parent.propertyId, $event)"
            >
              <div class="bt-card-header">
                <input
                  type="checkbox"
                  :checked="isCardSelected(group.parent.propertyId)"
                  @change="toggleCardSelection(group.parent.propertyId)"
                  class="bt-checkbox"
                />
                <div class="bt-card-address">
                  {{ group.parent.address }}
                </div>
                <div
                  v-if="group.parent.isParentAddr === 1"
                  class="bt-route-selects"
                >
                  <select
                    :value="group.parent.route || ''"
                    class="bt-route-select"
                    title="Route"
                    @change="
                      updatePropertyRoute(
                        group.parent.propertyId,
                        'route',
                        $event.target.value,
                      )
                    "
                  >
                    <option value="">—</option>
                    <option
                      v-for="r in 'ABCDEFG'.split('')"
                      :key="r"
                      :value="r"
                    >
                      {{ r }}
                    </option>
                  </select>
                  <select
                    :value="group.parent.routeSeq || ''"
                    class="bt-route-seq-select"
                    title="Route sequence"
                    @change="
                      updatePropertyRoute(
                        group.parent.propertyId,
                        'routeSeq',
                        $event.target.value
                          ? parseInt($event.target.value)
                          : null,
                      )
                    "
                  >
                    <option value="">—</option>
                    <option v-for="n in 99" :key="n" :value="n">{{ n }}</option>
                  </select>
                </div>
                <button
                  v-if="group.allChildren.length > 0"
                  class="bt-collapse-btn"
                  @click="toggleGroupCollapse(group.parent.propertyId)"
                  :title="
                    isGroupCollapsed(group.parent.propertyId)
                      ? 'Expand units'
                      : 'Collapse units'
                  "
                >
                  <i
                    class="fas"
                    :class="
                      isGroupCollapsed(group.parent.propertyId)
                        ? 'fa-chevron-right'
                        : 'fa-chevron-down'
                    "
                  ></i>
                  <span class="bt-child-count">{{
                    group.allChildren.length
                  }}</span>
                </button>
              </div>
              <div class="bt-card-body">
                <div v-if="group.parent.tenants.length" class="bt-tenants">
                  <span
                    v-for="(name, ti) in group.parent.tenants"
                    :key="ti"
                    class="bt-tenant-name"
                    >{{ name }}</span
                  >
                </div>
                <!-- Primary door lock for this property -->
                <div
                  class="bt-lock-row"
                  v-show="
                    !getDoorLocksForProperty(group.parent.propertyId).primary ||
                    lockMatchesFilter(
                      getDoorLocksForProperty(group.parent.propertyId).primary,
                      group.parent.address,
                      group.parent.tenants,
                      group.parent.propertyId,
                    )
                  "
                >
                  <template
                    v-if="
                      getDoorLocksForProperty(group.parent.propertyId).primary
                    "
                  >
                    <i
                      class="fas fa-sticky-note bt-note-icon"
                      :class="{
                        'has-note': getDoorLocksForProperty(
                          group.parent.propertyId,
                        ).primary.note,
                      }"
                      :title="
                        getDoorLocksForProperty(group.parent.propertyId).primary
                          .note || 'Add note'
                      "
                      @click="
                        openLockNotePopup(
                          getDoorLocksForProperty(group.parent.propertyId)
                            .primary.id,
                          getDoorLocksForProperty(group.parent.propertyId)
                            .primary.note,
                          group.parent.address,
                          getDoorLocksForProperty(group.parent.propertyId)
                            .primary.locationLabel,
                        )
                      "
                    ></i>
                    <input
                      type="text"
                      :value="
                        getDoorLocksForProperty(group.parent.propertyId).primary
                          .locationLabel || ''
                      "
                      placeholder="location"
                      class="bt-loc-input"
                      :disabled="!isCardFocused(group.parent.propertyId)"
                      @blur="
                        updateDoorLock(
                          getDoorLocksForProperty(group.parent.propertyId)
                            .primary.id,
                          { locationLabel: $event.target.value },
                        )
                      "
                    />
                    <select
                      :value="
                        getDoorLocksForProperty(group.parent.propertyId).primary
                          .lockType || ''
                      "
                      class="bt-type-select"
                      :disabled="!isCardFocused(group.parent.propertyId)"
                      @change="
                        updateDoorLock(
                          getDoorLocksForProperty(group.parent.propertyId)
                            .primary.id,
                          { lockType: $event.target.value },
                        )
                      "
                    >
                      <option value="">type...</option>
                      <option
                        v-for="lt in lockTypeOptions"
                        :key="lt"
                        :value="lt"
                      >
                        {{ lt }}
                      </option>
                    </select>
                    <label class="bt-done-label">
                      <input
                        type="checkbox"
                        :checked="
                          getDoorLocksForProperty(group.parent.propertyId)
                            .primary.batteryReplaced === 1
                        "
                        :disabled="!isCardFocused(group.parent.propertyId)"
                        @change="
                          updateDoorLock(
                            getDoorLocksForProperty(group.parent.propertyId)
                              .primary.id,
                            {
                              batteryReplaced: $event.target.checked ? 1 : 0,
                              ...(!$event.target.checked
                                ? { batteryReplacedDate: null }
                                : {}),
                            },
                          )
                        "
                      />
                      done
                    </label>
                    <span class="bt-date">{{
                      btFormatDate(
                        getDoorLocksForProperty(group.parent.propertyId).primary
                          .batteryReplacedDate,
                      )
                    }}</span>
                  </template>
                  <template v-else>
                    <button
                      class="bt-create-lock-btn"
                      @click="ensurePrimaryLock(group.parent.propertyId)"
                    >
                      <i class="fas fa-plus"></i> Add Lock
                    </button>
                  </template>
                </div>
                <!-- Child door locks -->
                <div
                  v-for="childLock in getDoorLocksForProperty(
                    group.parent.propertyId,
                  ).children"
                  :key="childLock.id"
                  class="bt-child-lock-row"
                  v-show="
                    lockMatchesFilter(
                      childLock,
                      group.parent.address,
                      group.parent.tenants,
                      group.parent.propertyId,
                    )
                  "
                >
                  <i
                    class="fas fa-sticky-note bt-note-icon"
                    :class="{ 'has-note': childLock.note }"
                    :title="childLock.note || 'Add note'"
                    @click="
                      openLockNotePopup(
                        childLock.id,
                        childLock.note,
                        group.parent.address,
                        childLock.locationLabel,
                      )
                    "
                  ></i>
                  <input
                    type="text"
                    :value="childLock.locationLabel || ''"
                    placeholder="location"
                    class="bt-loc-input"
                    :disabled="!isCardFocused(group.parent.propertyId)"
                    @blur="
                      updateDoorLock(childLock.id, {
                        locationLabel: $event.target.value,
                      })
                    "
                  />
                  <select
                    :value="childLock.lockType || ''"
                    class="bt-type-select"
                    :disabled="!isCardFocused(group.parent.propertyId)"
                    @change="
                      updateDoorLock(childLock.id, {
                        lockType: $event.target.value,
                      })
                    "
                  >
                    <option value="">type...</option>
                    <option v-for="lt in lockTypeOptions" :key="lt" :value="lt">
                      {{ lt }}
                    </option>
                  </select>
                  <label class="bt-done-label">
                    <input
                      type="checkbox"
                      :checked="childLock.batteryReplaced === 1"
                      :disabled="!isCardFocused(group.parent.propertyId)"
                      @change="
                        updateDoorLock(childLock.id, {
                          batteryReplaced: $event.target.checked ? 1 : 0,
                          ...(!$event.target.checked
                            ? { batteryReplacedDate: null }
                            : {}),
                        })
                      "
                    />
                    done
                  </label>
                  <span class="bt-date">{{
                    btFormatDate(childLock.batteryReplacedDate)
                  }}</span>
                  <button
                    class="bt-delete-child-btn"
                    @click="deleteDoorLock(childLock.id)"
                    title="Delete this lock"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
                <!-- Add child door lock button -->
                <button
                  v-if="
                    isCardFocused(group.parent.propertyId) &&
                    getDoorLocksForProperty(group.parent.propertyId).primary
                  "
                  class="bt-add-child-btn"
                  @click="
                    openAddChildModal(
                      group.parent.propertyId,
                      getDoorLocksForProperty(group.parent.propertyId).primary
                        .id,
                    )
                  "
                  title="Add child door lock"
                >
                  <i class="fas fa-plus"></i>
                </button>
                <div v-if="group.parent.scheduled" class="bt-scheduled-footer">
                  <i class="fas fa-calendar-alt"></i>
                  {{ formatScheduled(group.parent.scheduled) }}
                </div>
              </div>
            </div>

            <!-- Child property cards (apartments under parent building) -->
            <div
              v-show="!isGroupCollapsed(group.parent.propertyId)"
              class="bt-children-wrapper"
            >
              <div
                v-for="child in getVisibleChildren(group)"
                :key="child.propertyId"
                class="bt-card bt-card-child"
                :class="{
                  'bt-card-selected': isCardSelected(child.propertyId),
                  'bt-card-focused': isCardFocused(child.propertyId),
                }"
                @click="toggleFocusedCard(child.propertyId, $event)"
              >
                <div class="bt-card-header">
                  <input
                    type="checkbox"
                    :checked="isCardSelected(child.propertyId)"
                    @change="toggleCardSelection(child.propertyId)"
                    class="bt-checkbox"
                  />
                  <div class="bt-card-address">
                    {{ child.address }}
                  </div>
                </div>
                <div class="bt-card-body">
                  <div v-if="child.tenants.length" class="bt-tenants">
                    <span
                      v-for="(name, ti) in child.tenants"
                      :key="ti"
                      class="bt-tenant-name"
                      >{{ name }}</span
                    >
                  </div>
                  <div
                    class="bt-lock-row"
                    v-show="
                      !getDoorLocksForProperty(child.propertyId).primary ||
                      lockMatchesFilter(
                        getDoorLocksForProperty(child.propertyId).primary,
                        child.address,
                        child.tenants,
                        child.propertyId,
                      )
                    "
                  >
                    <template
                      v-if="getDoorLocksForProperty(child.propertyId).primary"
                    >
                      <i
                        class="fas fa-sticky-note bt-note-icon"
                        :class="{
                          'has-note': getDoorLocksForProperty(child.propertyId)
                            .primary.note,
                        }"
                        :title="
                          getDoorLocksForProperty(child.propertyId).primary
                            .note || 'Add note'
                        "
                        @click="
                          openLockNotePopup(
                            getDoorLocksForProperty(child.propertyId).primary
                              .id,
                            getDoorLocksForProperty(child.propertyId).primary
                              .note,
                            child.address,
                            getDoorLocksForProperty(child.propertyId).primary
                              .locationLabel,
                          )
                        "
                      ></i>
                      <input
                        type="text"
                        :value="
                          getDoorLocksForProperty(child.propertyId).primary
                            .locationLabel || ''
                        "
                        placeholder="location"
                        class="bt-loc-input"
                        :disabled="!isCardFocused(child.propertyId)"
                        @blur="
                          updateDoorLock(
                            getDoorLocksForProperty(child.propertyId).primary
                              .id,
                            { locationLabel: $event.target.value },
                          )
                        "
                      />
                      <select
                        :value="
                          getDoorLocksForProperty(child.propertyId).primary
                            .lockType || ''
                        "
                        class="bt-type-select"
                        :disabled="!isCardFocused(child.propertyId)"
                        @change="
                          updateDoorLock(
                            getDoorLocksForProperty(child.propertyId).primary
                              .id,
                            { lockType: $event.target.value },
                          )
                        "
                      >
                        <option value="">type...</option>
                        <option
                          v-for="lt in lockTypeOptions"
                          :key="lt"
                          :value="lt"
                        >
                          {{ lt }}
                        </option>
                      </select>
                      <label class="bt-done-label">
                        <input
                          type="checkbox"
                          :checked="
                            getDoorLocksForProperty(child.propertyId).primary
                              .batteryReplaced === 1
                          "
                          :disabled="!isCardFocused(child.propertyId)"
                          @change="
                            updateDoorLock(
                              getDoorLocksForProperty(child.propertyId).primary
                                .id,
                              {
                                batteryReplaced: $event.target.checked ? 1 : 0,
                                ...(!$event.target.checked
                                  ? { batteryReplacedDate: null }
                                  : {}),
                              },
                            )
                          "
                        />
                        done
                      </label>
                      <span class="bt-date">{{
                        btFormatDate(
                          getDoorLocksForProperty(child.propertyId).primary
                            .batteryReplacedDate,
                        )
                      }}</span>
                    </template>
                    <template v-else>
                      <button
                        class="bt-create-lock-btn"
                        @click="ensurePrimaryLock(child.propertyId)"
                      >
                        <i class="fas fa-plus"></i> Add Lock
                      </button>
                    </template>
                  </div>
                  <!-- Child door locks for apartment -->
                  <div
                    v-for="childLock in getDoorLocksForProperty(
                      child.propertyId,
                    ).children"
                    :key="childLock.id"
                    class="bt-child-lock-row"
                    v-show="
                      lockMatchesFilter(
                        childLock,
                        child.address,
                        child.tenants,
                        child.propertyId,
                      )
                    "
                  >
                    <i
                      class="fas fa-sticky-note bt-note-icon"
                      :class="{ 'has-note': childLock.note }"
                      :title="childLock.note || 'Add note'"
                      @click="
                        openLockNotePopup(
                          childLock.id,
                          childLock.note,
                          child.address,
                          childLock.locationLabel,
                        )
                      "
                    ></i>
                    <input
                      type="text"
                      :value="childLock.locationLabel || ''"
                      placeholder="location"
                      class="bt-loc-input"
                      :disabled="!isCardFocused(child.propertyId)"
                      @blur="
                        updateDoorLock(childLock.id, {
                          locationLabel: $event.target.value,
                        })
                      "
                    />
                    <select
                      :value="childLock.lockType || ''"
                      class="bt-type-select"
                      :disabled="!isCardFocused(child.propertyId)"
                      @change="
                        updateDoorLock(childLock.id, {
                          lockType: $event.target.value,
                        })
                      "
                    >
                      <option value="">type...</option>
                      <option
                        v-for="lt in lockTypeOptions"
                        :key="lt"
                        :value="lt"
                      >
                        {{ lt }}
                      </option>
                    </select>
                    <label class="bt-done-label">
                      <input
                        type="checkbox"
                        :checked="childLock.batteryReplaced === 1"
                        :disabled="!isCardFocused(child.propertyId)"
                        @change="
                          updateDoorLock(childLock.id, {
                            batteryReplaced: $event.target.checked ? 1 : 0,
                            ...(!$event.target.checked
                              ? { batteryReplacedDate: null }
                              : {}),
                          })
                        "
                      />
                      done
                    </label>
                    <span class="bt-date">{{
                      btFormatDate(childLock.batteryReplacedDate)
                    }}</span>
                    <button
                      class="bt-delete-child-btn"
                      @click="deleteDoorLock(childLock.id)"
                      title="Delete this lock"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                  <!-- Add child door lock button -->
                  <button
                    v-if="
                      isCardFocused(child.propertyId) &&
                      getDoorLocksForProperty(child.propertyId).primary
                    "
                    class="bt-add-child-btn"
                    @click="
                      openAddChildModal(
                        child.propertyId,
                        getDoorLocksForProperty(child.propertyId).primary.id,
                      )
                    "
                    title="Add child door lock"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                  <div v-if="child.scheduled" class="bt-scheduled-footer">
                    <i class="fas fa-calendar-alt"></i>
                    {{ formatScheduled(child.scheduled) }}
                  </div>
                </div>
              </div>
            </div>
            <!-- end bt-children-wrapper -->
          </div>

          <div
            v-if="!btGroupedProperties.length && !btLoading"
            class="bt-empty"
          >
            <i class="fas fa-battery-half bt-empty-icon"></i>
            <div>No properties found.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== CODE LOOKUP VIEW ===== -->
    <div v-if="activeView === 'code-lookup'">
      <!-- Phone Number Validation Modal -->
      <div v-if="showPhoneModal" class="modal-overlay">
        <div class="phone-modal">
          <h3>Phone Verification</h3>
          <p>Please enter your 10-digit phone number to access lock codes:</p>
          <input
            ref="phoneInputRef"
            v-model="phoneNumber"
            type="tel"
            placeholder="1234567890"
            maxlength="10"
            @input="phoneNumber = phoneNumber.replace(/\D/g, '')"
            @keyup.enter="submitPhoneNumber"
            class="phone-input"
          />
          <div v-if="phoneError" class="error-message">{{ phoneError }}</div>
          <div class="modal-buttons">
            <button @click="cancelPhoneModal" class="cancel-button">
              Cancel
            </button>
            <button
              @click="submitPhoneNumber"
              :disabled="isLoading"
              class="submit-button"
            >
              {{ isLoading ? 'Verifying...' : 'Submit' }}
            </button>
          </div>
        </div>
      </div>

      <!-- New Code Generation Modal -->
      <div v-if="showNewCodeModal" class="modal-overlay">
        <div class="new-code-modal">
          <h3>New Code</h3>
          <input
            v-model="newCodeValue"
            type="text"
            maxlength="6"
            class="new-code-input"
            @input="newCodeValue = newCodeValue.replace(/\D/g, '')"
          />
          <div class="modal-buttons">
            <button @click="cancelNewCode" class="cancel-button">Cancel</button>
            <button @click="saveNewCode" class="submit-button">Save</button>
          </div>
        </div>
      </div>

      <div class="title-bar">
        <button class="back-btn" @click="goBackToMenu">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span
          style="flex: 1; cursor: pointer"
          @click="handleTitleTap"
          @dblclick="console.log('Double click detected')"
          >Lock Codes</span
        >
        <button
          class="home"
          @click="refreshLockCodes"
          :disabled="isLoading"
          title="Refresh from server"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
      <div class="content">
        <div class="search-bar">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            @input="filterResults"
            placeholder="Search address..."
          />
          <button class="clear-search" @click="clearSearch">
            <i class="fas fa-backspace"></i>
          </button>
        </div>
        <!-- Examples -->
        <div v-if="!isSearching">
          <div class="example">
            Example:<br /><br />
            type <strong style="color: #907793">121 Mad 15 </strong> to find 121
            Madison Apt 15
          </div>
          <div class="example-indent">
            you can also type <strong style="color: #907793">furn</strong> to
            find furnace,
          </div>
          <div class="example-indent">
            or <strong style="color: #907793">gar</strong> to find garage,
          </div>
          <div class="example-indent">
            or <strong style="color: #907793">boi</strong> to find boiler, etc
          </div>

          <!-- Empty state with padlock icon - hidden when search results appear -->
          <div class="empty-state" v-if="!filteredLockCodes.length">
            <i class="fas fa-lock empty-icon"></i>
            <div>
              Search for lock codes by address.<br />Tap a search result to
              display the codes.
            </div>
          </div>
        </div>
        <div v-show="filteredLockCodes.length" class="match-label">
          Matches:
        </div>
        <ul v-if="filteredLockCodes.length && !selectedCode">
          <li
            v-for="code in filteredLockCodes.slice(0, 8)"
            :key="code.id"
            @click="selectCode(code)"
          >
            <span
              v-html="
                highlightMatch(
                  code.address + ' ' + (code.note ? code.note : ''),
                  searchQuery,
                )
              "
            ></span>
          </li>
        </ul>

        <!-- Nothing Found State -->
        <div v-if="nothingFound" class="nothing-found">
          <i class="fas fa-exclamation-triangle"></i>
          <div>Nothing found.</div>
          <div v-if="canCreateLockCode" class="nothing-found-address">
            {{ missingLockProperty.address }}
          </div>
          <button
            v-if="canCreateLockCode"
            type="button"
            class="create-lock-code"
            :disabled="isLoading"
            @click="createFirstLockCode"
          >
            Add lock code
          </button>
        </div>

        <div v-if="selectedCode" class="details-form">
          <div class="form-group">
            <label>Address:</label>
            <div>
              {{ selectedCode.address }}
            </div>
          </div>
        </div>

        <div v-if="selectedCode" class="details-form">
          <div class="form-group">
            <label>Note:</label>
            <input
              v-model="selectedCode.note"
              :disabled="!isEditing"
              :class="{ editable: isEditing, readonly: !isEditing }"
              @blur="updateLockCode('note')"
            />
          </div>
          <div class="form-group" v-if="role < 3">
            <label>Tenant Code:</label>
            <div class="input-with-button">
              <input
                v-model="selectedCode.tenantCode"
                ref="tenantCodeInputRef"
                :disabled="!isEditing"
                :class="{ editable: isEditing, readonly: !isEditing }"
                class="narrow-input"
                @blur="updateLockCode('tenantCode')"
              />
              <button @click="openNewCodeModal('tenant')" class="new-button">
                New
              </button>
            </div>
          </div>
          <div class="form-group">
            <label>Maint Code:</label>
            <div class="input-with-button">
              <input
                v-model="selectedCode.maintCode"
                :disabled="!isEditing"
                :class="{ editable: isEditing, readonly: !isEditing }"
                class="narrow-input"
                @blur="updateLockCode('maintCode')"
              />
              <button @click="openNewCodeModal('maint')" class="new-button">
                New
              </button>
            </div>
          </div>
          <div class="form-group">
            <label>Slot Info:</label>
            <input
              v-model="selectedCode.slotNumber"
              :disabled="!isEditing"
              :class="{ editable: isEditing, readonly: !isEditing }"
              @blur="updateLockCode('slotNumber')"
            />
          </div>
          <div class="form-group">
            <label>Slot or Date :</label>
            <input
              v-model="selectedCode.slotNumberOrDate"
              :disabled="!isEditing"
              :class="{ editable: isEditing, readonly: !isEditing }"
              @blur="updateLockCode('slotNumberOrDate')"
            />
          </div>
          <div class="form-group">
            <label>Battery Changed:</label>
            <input
              type="date"
              v-model="batteryChangedDate"
              :disabled="!isEditing"
              :class="{ editable: isEditing, readonly: !isEditing }"
              @blur="updateBatteryChanged"
            />
          </div>
          <div class="modified-date">
            updated: {{ formatDate(selectedCode.modifiedOn) }} by
            {{ selectedCode.displayName }}
          </div>
          <!-- Edit Button -->
          <button v-if="role < 3" class="edit-button" @click="toggleEditMode">
            {{ isEditing ? 'Stop Editing' : 'Edit' }}
          </button>
        </div>
      </div>

      <!-- Lock Code Requests Popup -->
      <div v-if="showRequestsPopup" class="requests-popup-overlay">
        <div class="requests-popup">
          <div class="requests-header">
            <span>Lock Code Requests</span>
            <button class="close-btn" @click="closeRequestsPopup">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="requests-search">
            <input
              v-model="requestsSearchQuery"
              placeholder="Search requests..."
              class="requests-search-input"
            />
          </div>
          <div class="requests-list">
            <div
              v-for="request in filteredRequests"
              :key="request.id"
              class="request-card"
            >
              <div class="request-details">
                <span
                  class="request-user"
                  v-html="
                    highlightMatch(
                      request.displayName || 'Unknown',
                      requestsSearchQuery,
                    )
                  "
                ></span>
                <span
                  class="request-address"
                  v-html="highlightMatch(request.address, requestsSearchQuery)"
                ></span>
                <span class="request-date">{{
                  formatDate(request.requestedOn)
                }}</span>
              </div>
            </div>
            <div v-if="filteredRequests.length === 0" class="no-requests">
              No requests found
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- end code-lookup view -->
  </div>
</template>

<style scoped>
.lock-codes-page {
  background-color: #ffffff;
  max-width: 440px;
  height: 100vh;
}

body {
  background-color: #f0f0f0 !important;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f3f3f3;
  max-width: 420px;
}

.content {
  padding-top: 8px;
  padding-left: 20px;
}

.title-bar {
  background: #6e5875;
  color: #fff;
  font-size: 1.4em;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-bottom: 10px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 10px;
}

.search-bar input {
  margin-left: -10px;
}

ul {
  margin-top: 10px;
  list-style-type: none;
  margin: 0;
  padding: 0;
}

li {
  cursor: pointer;
  padding: 10px;
  border-radius: 5px;
  border: 1pt solid #ddd;
  margin-bottom: 8px;
  background: #ddd;
  width: 88%;
}

.details-form {
  display: flex;
  flex-direction: column;
  margin-top: 25px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px; /* Space between label/input groups */
}

label {
  margin-bottom: 4px; /* Space between label and input */
}

input,
button {
  padding: 6px;
  border-radius: 4px;
  border: 1pt solid #d4d4d4;
  outline: none;
  font-size: 1.2em;
}

input {
  width: 300px;
}

.narrow-input {
  width: 240px; /* 60px narrower than default 300px */
}

.input-with-button {
  display: flex;
  gap: 10px;
  align-items: center;
}

.new-button {
  background: #a898b7;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.9em;
  cursor: pointer;
  height: 35px; /* Match input height */
}

.new-button:hover {
  background: #8e7b91;
}

.editable {
  background-color: white; /* Editable input style */
}

.readonly {
  background-color: lightgray; /* Read-only input style */
}

:deep(.highlight) {
  background-color: #a898b7;
}

.example {
  font-size: 0.8em;
  margin-top: 10px;
  margin-bottom: 10px;
  margin-top: 10px;
}

.example-indent {
  font-size: 0.8em;
  margin-top: -10px;
  margin-bottom: 10px;
  margin-left: 40px;
}

.match-label {
  margin-top: 20px;
  margin-bottom: 10px;
  color: #907793;
}

.edit-button {
  margin-top: 15px;
  width: 315px;
  background: #a898b7;
  color: #fff;
}

.home {
  height: 30px;
  width: 30px;
  font-size: 0.6em;
  border-radius: 15px;
  color: #fff;
  background: #907793;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

/* Fix for FontAwesome icons in buttons */
.title-bar button i,
.clear-search i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.modified-date {
  font-size: 0.9em;
}

.clear-search {
  background: #a080a8;
  color: #fff;
  border-radius: 5px;
  padding: 5px;
  font-size: 1.2em;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  text-align: center;
  height: 50vh; /* Make it take up half the viewport height */
}

.empty-icon {
  font-size: 6em;
  color: #d9d9d9;
  margin-bottom: 15px;
}

.empty-state div {
  color: #777777; /* Medium grey color for the text */
}

/* Phone Modal Styles */
.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 440px;
  z-index: 100000;
}

.phone-modal {
  margin-top: -150px;
  background: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 330px;
  width: 90%;
  text-align: center;
}

.phone-modal h3 {
  margin-top: 0;
  color: #a080a8;
}

.phone-modal p {
  margin-bottom: 20px;
  color: #666;
}

.phone-input {
  width: 100%;
  padding: 10px;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 1.2em;
  text-align: center;
  margin-bottom: 10px;
}

.phone-input:focus {
  border-color: #a080a8;
  outline: none;
}

.error-message {
  color: #d32f2f;
  font-size: 0.9em;
  margin-bottom: 15px;
}

.modal-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.cancel-button,
.submit-button {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 1em;
  cursor: pointer;
}

.cancel-button {
  background: #ccc;
  color: #666;
}

.submit-button {
  background: #a080a8;
  color: white;
}

.submit-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Nothing Found Styles */
.nothing-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 40px 0;
  text-align: center;
  color: #999;
}

.nothing-found i {
  font-size: 3em;
  margin-bottom: 15px;
  color: #d9d9d9;
}

.nothing-found div {
  font-size: 1.1em;
}

.nothing-found-address {
  margin-top: 8px;
  color: #666;
}

.create-lock-code {
  margin-top: 16px;
  background: #a898b7;
  color: #fff;
  border: none;
  padding: 8px 16px;
  cursor: pointer;
}

.create-lock-code:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* New Code Modal Styles */
.new-code-modal {
  background: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 280px;
  width: 90%;
  text-align: center;
}

.new-code-modal h3 {
  margin-top: 0;
  color: #a080a8;
}

.new-code-input {
  width: 100%;
  padding: 10px;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 1.4em;
  text-align: center;
  margin-bottom: 20px;
  font-family: monospace;
  letter-spacing: 2px;
}

.new-code-input:focus {
  border-color: #a080a8;
  outline: none;
}

/* Lock Code Requests Popup Styles */
.requests-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.requests-popup {
  background: #fff;
  width: 90%;
  max-width: 440px;
  height: 90vh;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.requests-header {
  background: #a080a8;
  color: #fff;
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-size: 1.1em;
  font-weight: bold;
}

.close-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 1.3em;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  opacity: 0.8;
}

.requests-search {
  padding: 10px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

.requests-search-input {
  width: 91%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9em;
}

.requests-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.requests-list::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.request-card {
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 6px;
  font-size: 0.8em;
}

.request-details {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 0.9em;
  flex-wrap: nowrap;
}

.request-user {
  font-weight: bold;
  color: #333;
  white-space: nowrap;
}

.request-address {
  flex: 1;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.request-date {
  color: #999;
  font-size: 0.85em;
  white-space: nowrap;
}

.no-requests {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 1em;
}

.requests-list .highlight {
  background-color: #ffeb3b;
  padding: 1px 2px;
  border-radius: 2px;
}

/* Menu View Styles */
.menu-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 40px 20px;
  margin-top: 20px;
}

.menu-button {
  width: 90%;
  max-width: 360px;
  padding: 30px 20px;
  border: none;
  border-radius: 12px;
  background: #6e5875;
  color: #fff;
  font-size: 1.4em;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.menu-button:active {
  transform: scale(0.97);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.menu-icon {
  font-size: 2em;
}

/* Back Button */
.back-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 0.8em;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
  margin-right: 4px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Coming Soon Placeholder */
.coming-soon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 60vh;
  color: #999;
  font-size: 1.3em;
}

.coming-soon-icon {
  font-size: 5em;
  color: #d9d9d9;
  margin-bottom: 20px;
}

/* ===== Battery Tracker Styles ===== */
.bt-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.bt-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  overflow: hidden;
}

.bt-filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.bt-filter-input {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #d4d4d4;
  border-radius: 6px;
  font-size: 1em;
  outline: none;
}

.bt-filter-input:focus {
  border-color: #a080a8;
}

.bt-button-row {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.bt-filter-btn {
  padding: 6px 14px;
  border: 1px solid #c4b5ce;
  border-radius: 16px;
  background: #f5f0f7;
  color: #6e5875;
  font-size: 0.85em;
  cursor: pointer;
  font-weight: 500;
  min-width: 100px;
  text-align: center;
}

.bt-filter-btn:active,
.bt-filter-btn.active,
.bt-filter-btn.bt-filter-active {
  background: #6e5875;
  color: #fff;
}

.bt-action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: #6e5875;
  color: #fff;
  font-size: 0.85em;
  cursor: pointer;
  font-weight: 500;
}

.bt-action-btn:active {
  background: #5a4862;
}

.bt-loading {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 1.1em;
}

.bt-card-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 120px;
}

.bt-card-list::-webkit-scrollbar {
  display: none;
}

.bt-group {
  margin-bottom: 2px;
}

.bt-card {
  background: #fff;
  border: 2px solid #c4b5ce;
  border-radius: 6px;
  padding: 6px 8px;
  margin-bottom: 3px;
  transition: border-color 0.15s;
}

.bt-card-selected {
  border-color: #6e5875;
  background: #faf7fc;
}

.bt-card-focused {
  outline: 2px solid limegreen;
  outline-offset: -1px;
}

.bt-card :disabled {
  opacity: 0.65;
  cursor: default;
  color: #444;
}

.bt-card-child {
  margin-left: 0;
  border: 1px solid #e0e0e0;
  border-left: 3px solid #d9cfe0;
}

.bt-children-wrapper {
  overflow: visible;
}

.bt-collapse-btn {
  background: none;
  border: none;
  color: #907793;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 0.8em;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  flex-shrink: 0;
}

.bt-collapse-btn:active {
  opacity: 0.6;
}

.bt-child-count {
  background: #d9cfe0;
  color: #5a3e63;
  border-radius: 10px;
  padding: 1px 6px;
  font-size: 0.85em;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
}

.bt-card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}

.bt-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #6e5875;
  flex-shrink: 0;
}

.bt-card-address {
  font-weight: 600;
  font-size: 0.88em;
  color: #333;
  flex: 1;
}

.bt-route-selects {
  display: flex;
  gap: 3px;
  flex-shrink: 0;
  margin-left: auto;
}

.bt-route-select,
.bt-route-seq-select {
  padding: 1px 2px;
  font-size: 0.75em;
  border: 1px solid #ddd;
  border-radius: 3px;
  height: 22px;
  color: #555;
}

.bt-route-select {
  width: 36px;
}

.bt-route-seq-select {
  width: 44px;
}

.bt-scheduled-tag {
  font-weight: 400;
  font-size: 0.85em;
  color: #b8860b;
}

.bt-scheduled-footer {
  font-size: 0.78em;
  color: white;
  background: #a080a8;
  margin-top: 4px;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 6px;
  border-radius: 4px;
}

.bt-scheduled-footer i {
  margin-right: 3px;
}

.bt-action-active {
  background: #6e5875;
  color: #fff;
  border-color: #6e5875;
}

.bt-card-body {
  padding-left: 24px;
  position: relative;
  padding-bottom: 4px;
}

.bt-tenants {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin-bottom: 3px;
}

.bt-tenant-name {
  background: #eee8f1;
  color: #555;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 0.75em;
}

.bt-lock-row,
.bt-child-lock-row {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: nowrap;
  margin-bottom: 2px;
}

.bt-child-lock-row {
  margin-left: -5px;
  padding-left: 3px;
  border-left: 2px solid #e8e0ee;
}

.bt-loc-input {
  width: 68px;
  min-width: 40px;
  flex-shrink: 1;
  padding: 2px 4px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 0.8em;
  height: 24px;
  box-sizing: border-box;
}

.bt-type-select {
  padding: 2px 2px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 0.75em;
  background: #fff;
  height: 24px;
  box-sizing: border-box;
  max-width: 120px;
  min-width: 0;
  flex-shrink: 1;
}

.bt-note-icon {
  cursor: pointer;
  font-size: 0.85em;
  color: #ccc;
  flex-shrink: 0;
}

.bt-note-icon.has-note {
  color: #e8a838;
}

.bt-note-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
}

.bt-note-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  padding: 16px;
  z-index: 1000;
  width: 340px;
  max-width: 90vw;
}

.bt-note-popup h4 {
  margin: 0 0 4px 0;
  font-size: 0.95em;
  color: #6e5875;
}

.bt-note-context {
  font-size: 0.82em;
  color: #888;
  margin-bottom: 10px;
  line-height: 1.3;
}

.bt-note-popup textarea {
  width: 100%;
  height: 120px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  font-size: 0.85em;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;
}

.bt-note-popup-buttons {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 10px;
}

.bt-note-popup-buttons button {
  padding: 5px 14px;
  border: none;
  border-radius: 4px;
  font-size: 0.85em;
  cursor: pointer;
}

.bt-note-popup-buttons button:first-child {
  background: #6e5875;
  color: #fff;
}

.bt-note-popup-buttons button:last-child {
  background: #eee;
  color: #333;
}

.bt-done-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78em;
  line-height: 1;
  color: #666;
  white-space: nowrap;
  flex-shrink: 0;
}

.bt-done-label input[type='checkbox'] {
  accent-color: #6e5875;
  margin: 0;
  width: 14px;
  height: 14px;
  vertical-align: middle;
}

.bt-done-label input[type='checkbox']:not(:checked) {
  appearance: none;
  -webkit-appearance: none;
  border: 2px solid #e8a0a0;
  border-radius: 3px;
  background: #fff;
}

.bt-date {
  font-size: 0.75em;
  line-height: 1;
  color: #999;
  white-space: nowrap;
  margin-top: -3px;
}

.bt-add-child-btn {
  display: block;
  margin-left: auto;
  margin-top: 4px;
  background: none;
  border: 1px solid #d4c8db;
  border-radius: 4px;
  color: #907793;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 0.8em;
}

.bt-delete-child-btn {
  background: none;
  border: 1px solid #d4c8db;
  border-radius: 4px;
  color: #907793;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 0.8em;
  margin-left: auto;
  display: none;
}

.bt-card-focused .bt-delete-child-btn {
  display: inline-flex;
  color: #d9cfe0;
  border-color: #d9cfe0;
}

.bt-create-lock-btn {
  background: #f5f0f7;
  border: 1px dashed #c4b5ce;
  border-radius: 6px;
  color: #6e5875;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 0.85em;
}

.bt-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 40vh;
  color: #999;
}

.bt-empty-icon {
  font-size: 4em;
  color: #d9d9d9;
  margin-bottom: 12px;
}

/* Battery Tracker Modals */
.bt-child-modal,
.bt-notify-modal {
  background: #fff;
  padding: 24px;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  max-width: 520px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.bt-notify-modal::-webkit-scrollbar {
  display: none;
}

.bt-child-modal h3,
.bt-notify-modal h3 {
  margin-top: 0;
  color: #6e5875;
}

.bt-child-input {
  width: 100%;
  padding: 10px;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 1.1em;
  margin-bottom: 16px;
}

.bt-child-input:focus {
  border-color: #a080a8;
  outline: none;
}

.bt-label {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
  color: #555;
  font-size: 0.9em;
}

.bt-email-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 0.9em;
  font-family: monospace;
  resize: vertical;
  margin-bottom: 12px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  box-sizing: border-box;
}

.bt-email-textarea::-webkit-scrollbar {
  display: none;
}

.bt-notify-count {
  font-size: 0.75em;
  font-weight: 400;
  color: #999;
}

.bt-email-preview {
  border: 1px solid #e0d8e4;
  border-radius: 6px;
  padding: 16px;
  background: #faf8fc;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  margin-bottom: 12px;
  font-size: 0.85em;
  line-height: 1.5;
}

.bt-email-preview::-webkit-scrollbar {
  display: none;
}

.bt-email-preview h1,
.bt-email-preview h2,
.bt-email-preview h3 {
  color: #6e5875;
  margin-top: 0;
}

.bt-fee-select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1em;
  margin-bottom: 16px;
  width: 120px;
}

.bt-notify-info {
  color: #888;
  font-size: 0.9em;
  margin-bottom: 16px;
}

.bt-notify-results h4 {
  margin-top: 0;
  color: #6e5875;
}

.bt-notify-result-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #eee;
  font-size: 0.9em;
}

.status-sent {
  color: #28a745;
}

.status-failed {
  color: #dc3545;
}

.status-none {
  color: #999;
}

/* Save-status toast */
.lc-toast {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  font-size: 14px;
  color: #fff;
  max-width: 90vw;
  pointer-events: none;
}
.lc-toast-success {
  background: #3c8f47;
}
.lc-toast-error {
  background: #c0392b;
}
.lc-toast i {
  font-size: 16px;
}
.lc-toast-enter-active,
.lc-toast-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.lc-toast-enter-from,
.lc-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -10px);
}
</style>
